import { countsFor, formatMoney, type OrgModel, type Person } from '@tive/core';
import { colorKeyFor, swatchFor, tintOf } from '../lib/colors';
import { placeLabel } from '../lib/utils';
import type { ColumnDef } from '../lib/table';
import { useAppStore } from '../store/useAppStore';
import { DataTable } from './DataTable';

/**
 * Everyone in the scenario, as rows.
 *
 * The third view of the same data, and it exists because the other two answer
 * "who reports to whom" and "who works on what" and neither answers "show me all
 * of this at once". A table is also the only one of the three you can read
 * without moving anything.
 */
export function PeopleTable() {
  const model = useAppStore((s) => s.model);
  const filtered = useAppStore((s) => s.filtered);
  const squads = useAppStore((s) => s.planSquads);
  const colorBy = useAppStore((s) => s.colorBy);
  const overrides = useAppStore((s) => s.colorOverrides);
  const movedIds = useAppStore((s) => s.movedIds);
  const departures = useAppStore((s) => s.scenario?.departures);
  const select = useAppStore((s) => s.select);
  const openPanel = useAppStore((s) => s.openPanel);
  const setView = useAppStore((s) => s.setView);
  const revealPerson = useAppStore((s) => s.revealPerson);

  /*
   * The same people the chart is showing, filters and all. A table that ignored
   * the filters would disagree with the view somebody just set up, and the
   * headcount at the top of the two would not match.
   */
  const rows = (model?.people ?? []).filter(
    (person) => filtered.keep === null || filtered.keep.has(person.id),
  );

  const leaving = new Set(departures ?? []);
  const columns = peopleColumns(model, squads, movedIds, leaving);

  return (
    <div className="h-full min-h-0 bg-[var(--color-canvas)]">
      <DataTable
        table="people"
        columns={columns}
        rows={rows}
        rowKey={(person) => person.id}
        filename="people"
        /*
         * Coloured by whatever the chart is coloured by, at the same 8% as a
         * card's wash. Following the toolbar rather than having its own control
         * is the point: it is one decision about what colour means, and the
         * table is another way of looking at the same org.
         */
        rowTone={(person) => {
          const key = colorKeyFor(person, colorBy, squads);
          if (!key) return null;
          return tintOf(swatchFor(key, overrides, colorBy), 8);
        }}
        /*
         * A click opens the details pane and leaves you here. Reading down a
         * table is a thing people do for minutes at a time, and being thrown
         * onto the chart by a single click makes it impossible — you lose your
         * place to answer one question about one row.
         *
         * Going to the chart is still one gesture away, and the details pane
         * carries a button for it as well, since a click is not a discoverable
         * way to be told about a double one.
         */
        onRowClick={(person) => {
          select(person.id);
          openPanel('details');
        }}
        onRowDoubleClick={(person) => {
          select(person.id);
          setView('reporting');
          revealPerson(person.id);
        }}
        empty={{
          title: 'Nobody to show',
          hint: 'Every person is filtered out. Clear the filters to see them again.',
        }}
      />
    </div>
  );
}

/**
 * The columns, and which of them a table starts with.
 *
 * Everything the details pane can show is here; most of it is `hiddenByDefault`,
 * because a table of twenty columns is as hard to read as a chart of three
 * hundred cards and the point of the picker is that nobody has to agree with me
 * about which eight matter.
 */
export function peopleColumns(
  model: OrgModel | null,
  squads: Parameters<typeof colorKeyFor>[2],
  movedIds: ReadonlySet<string>,
  leaving: ReadonlySet<string>,
): ColumnDef<Person>[] {
  const nameOf = (id: string | null | undefined) => (id ? (model?.byId.get(id)?.name ?? null) : null);
  const counts = (person: Person) => (model ? countsFor(model, person.id) : null);

  return [
    { key: 'name', label: 'Name', value: (person) => person.name },
    { key: 'title', label: 'Title', value: (person) => person.title },
    { key: 'manager', label: 'Manager', value: (person) => nameOf(person.managerId) },
    { key: 'department', label: 'Department', value: (person) => person.department },
    {
      key: 'teams',
      label: 'Teams',
      value: (person) =>
        squads.squads
          .filter((squad) => squad.members.some((member) => member.personId === person.id))
          .map((squad) => squad.name)
          .join(', ') || null,
    },
    { key: 'location', label: 'Location', value: (person) => placeLabel(person.location, null) },
    {
      key: 'directReports',
      label: 'Direct',
      align: 'right',
      value: (person) => counts(person)?.directReports ?? 0,
    },
    {
      key: 'totalReports',
      label: 'Total org',
      align: 'right',
      value: (person) => counts(person)?.totalReports ?? 0,
    },
    {
      key: 'state',
      label: 'State',
      /*
       * Off to start with. It is empty for almost everybody — only the handful a
       * plan actually touches have anything in it — and a column that is blank
       * down its whole length costs more width than it pays back.
       */
      hiddenByDefault: true,
      value: (person) => {
        if (person.isNew) return 'Planned';
        if (leaving.has(person.id)) return 'Leaving';
        if (movedIds.has(person.id)) return 'Moved';
        return null;
      },
    },
    { key: 'notes', label: 'Notes', value: (person) => person.notes ?? null },

    // --- and the rest, for when somebody is looking for it -----------------
    { key: 'country', label: 'Country', value: (person) => person.country, hiddenByDefault: true },
    { key: 'email', label: 'Email', value: (person) => person.email, hiddenByDefault: true },
    {
      key: 'employeeNumber',
      label: 'Employee #',
      value: (person) => person.employeeNumber,
      hiddenByDefault: true,
    },
    { key: 'startDate', label: 'Start date', value: (person) => person.startDate, hiddenByDefault: true },
    { key: 'status', label: 'Status', value: (person) => person.status, hiddenByDefault: true },
    {
      key: 'hrisTeams',
      label: 'HRIS teams',
      value: (person) => person.teams.join(', ') || null,
      hiddenByDefault: true,
    },
    {
      key: 'dotted',
      label: 'Also answers to',
      value: (person) => (person.dottedManagerIds ?? []).map((id) => nameOf(id) ?? id).join(', ') || null,
      hiddenByDefault: true,
    },
    {
      key: 'budget',
      label: 'Budget',
      align: 'right',
      secret: true,
      // Only a planned role has one, and deliberately: real pay is not read from
      // the HRIS, so this column is empty for everybody who already works here.
      value: (person) => (person.plannedCost ? formatMoney(person.plannedCost) : null),
      hiddenByDefault: true,
    },
    {
      key: 'reasonKind',
      label: 'Net new / backfill',
      value: (person) =>
        person.plannedReason ? (person.plannedReason.kind === 'backfill' ? 'Backfill' : 'Net new') : null,
      hiddenByDefault: true,
    },
    {
      key: 'reason',
      label: 'Justification / backfilling',
      value: (person) =>
        person.plannedReason
          ? person.plannedReason.kind === 'backfill'
            ? person.plannedReason.backfillFor
            : person.plannedReason.justification
          : null,
      hiddenByDefault: true,
    },
  ];
}
